import { useState } from 'react';
import { X, Pause, Coffee, Clock, CalendarOff, AlertTriangle } from 'lucide-react';
import { formatDate } from '@/utils/dates';
import { useLanguage } from '@/hooks/useLanguage';

interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPause: (months: 1 | 2 | 3 | 'indefinite') => Promise<void>;
  onCancel: () => Promise<void>;
  subscriptionExpiresAt?: string | null;
}

export function CancelModal({ isOpen, onClose, onPause, onCancel, subscriptionExpiresAt }: CancelModalProps) {
  const { t } = useLanguage();
  const [confirming, setConfirming] = useState(false);
  if (!isOpen) return null;

  const paidUntilDate = subscriptionExpiresAt
    ? formatDate(subscriptionExpiresAt)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-[var(--bg-card)] border border-[var(--border)] shadow-2xl animate-fade-in">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-lg hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          <div className="text-center mb-5">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--accent-teal)]/10 mb-3">
              <Coffee className="w-6 h-6 text-[var(--accent-teal)]" />
            </div>
            <h3 className="text-lg font-semibold tracking-tight text-[var(--text-primary)] mb-1">
              {t.cancel.takingABreak}
            </h3>
            <p className="text-sm text-[var(--text-secondary)]">
              {t.cancel.pauseDescription}
            </p>
          </div>

          {paidUntilDate && (
            <div className="flex items-center gap-2 px-3 py-2 mb-4 rounded-lg bg-[var(--accent-gold)]/10 border border-[var(--accent-gold)]/20">
              <Clock className="w-3.5 h-3.5 text-[var(--accent-gold)] flex-shrink-0" />
              <p className="text-xs text-[var(--accent-gold)]">
                {t.cancel.proActiveUntil.replace('{date}', paidUntilDate)}
              </p>
            </div>
          )}

          <div className="space-y-2 mb-3">
            {([1, 2, 3] as const).map(months => {
              const resumeDate = subscriptionExpiresAt
                ? formatDate(new Date(subscriptionExpiresAt).getTime() + months * 30 * 24 * 60 * 60 * 1000)
                : null;

              return (
                <button
                  key={months}
                  onClick={() => onPause(months)}
                  className="w-full py-3 px-4 rounded-xl border border-[var(--border)] hover:border-[var(--accent-teal)]/50 hover:bg-[var(--accent-teal)]/5 text-sm font-medium text-[var(--text-primary)] transition-all flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Pause className="w-4 h-4 text-[var(--accent-teal)]" />
                    <span>{(months > 1 ? t.cancel.pauseMonthsPlural : t.cancel.pauseMonths).replace('{count}', String(months))}</span>
                  </div>
                  <span className="text-[11px] text-[var(--text-secondary)]">
                    {resumeDate ? t.cancel.resumesOn.replace('{date}', resumeDate) : t.cancel.resumeAnytime}
                  </span>
                </button>
              );
            })}

            <button
              onClick={() => onPause('indefinite')}
              className="w-full py-3 px-4 rounded-xl border border-dashed border-[var(--border)] hover:border-[var(--accent-teal)]/50 hover:bg-[var(--accent-teal)]/5 text-sm font-medium text-[var(--text-primary)] transition-all flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                <CalendarOff className="w-4 h-4 text-[var(--text-secondary)]" />
                <span>{t.cancel.pauseIndefinitely}</span>
              </div>
              <span className="text-[11px] text-[var(--text-secondary)]">{t.cancel.resumeWhenReady}</span>
            </button>
          </div>

          <p className="text-[10px] text-[var(--text-secondary)]/60 text-center mb-4">
            {t.cancel.resumeFromSettings}
          </p>

          {confirming ? (
            <div role="alertdialog" aria-labelledby="cancel-confirm-title" className="rounded-xl border border-[var(--status-red)]/30 bg-[var(--status-red)]/5 p-3 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[var(--status-red)] flex-shrink-0 mt-0.5" />
                <div>
                  <p id="cancel-confirm-title" className="text-sm font-semibold text-[var(--text-primary)]">{t.cancel.confirmTitle}</p>
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    {paidUntilDate ? t.cancel.confirmBody.replace('{date}', paidUntilDate) : t.cancel.confirmBodyNoDate}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setConfirming(false)}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/80 transition-colors"
                >
                  {t.cancel.keepPro}
                </button>
                <button
                  onClick={onCancel}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold border border-[var(--status-red)]/40 text-[var(--status-red)] hover:bg-[var(--status-red)]/10 transition-colors"
                >
                  {t.cancel.confirmCancel}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="w-full text-center text-xs text-[var(--text-secondary)]/60 hover:text-[var(--status-red)] transition-colors py-2"
            >
              {t.cancel.cancelInstead}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
